import type { IStrategy } from '../types/strategy';
import { ATTRIBUTE_DATABASE_NAME, ATTRIBUTE_PROPER_NAME } from '../types/strategy';
import { TimeSeriesEntityTypeConstants } from '@/constants/TimeSeriesEntityTypeConstants';
import useTranslation from '@/composables/useTranslation';
import useFxConversion from '@/composables/useFxConversion';
import { FxConversion } from '@/constants/FxConversion';
import type {
  DSBStrategyItemResponseDTO,
  PortfolioItemResponseDTO,
  StrategyItemResponseDTO,
} from '@/api-v2/web/discover';
import { TrackTypeConstants } from '@/constants/TrackTypeConstants';
import type { SignalListItemResponseDTO } from '@/api-v2/admin/strategies';
import type { StrategyFactsheetBasicInfoDTO } from '@/api-v2/web/strategies';
import { DisplayName } from '@/types/settings/DisplayNamePreference';
import type { StrategyItemSubset } from '@/types/StrategyItemSubset';
import { getStrategyDisplayCode } from './staticInfo';
import type { IPortfolioTreeStrategyStaticInfo, IPortfolioTreeSubportfolio } from '@/types/IPortfolioTree';
import type { StrategyDisplayNameSource } from '@/types/StrategyDisplayNameSource';

export interface StrategyStaticInfo {
  code: string;
  trackType: TrackTypeConstants;
  type: TimeSeriesEntityTypeConstants;
  internalReference?: string | null;
  isin?: string[];
  morningStarId?: string[];
  ticker?: string[];
  ric?: string[];
  sedol?: string[];
}

/**
 * Get the identifier entry for given strategy.
 *
 * See WAA-2779 for the rules for selecting identifier.
 */
export const getIdentifier = (strategy: StrategyStaticInfo): { name: string; value: string } => {
  if (strategy.trackType === TrackTypeConstants.PRIVATE_TRACK || strategy.trackType === TrackTypeConstants.PROXY) {
    // Prefer Internal Identifier > Index Code (ticker) > ISIN > MStar ID
    if (strategy.internalReference) {
      return { name: 'Internal Identifier', value: strategy.internalReference };
    }

    if (strategy.ticker?.length) {
      // Yes the label is "Index Code" because the platform already mixed
      // up bloomberg ticker/mstar ID/etc as "Index Code" in various place.
      // Trying to be consistently wrong here.
      return { name: 'Index Code', value: strategy.ticker[0] };
    }

    if (strategy.isin?.length) {
      return { name: 'ISIN', value: strategy.isin[0] };
    }

    if (strategy.morningStarId?.length) {
      return { name: 'Morningstar ID', value: strategy.morningStarId[0] };
    }

    return { name: 'Code', value: strategy.code };
  }

  switch (strategy.type) {
    case TimeSeriesEntityTypeConstants.FUND: {
      // Prefer ISIN, fallback to MStar ID
      if (strategy.isin?.length) {
        return { name: 'ISIN', value: strategy.isin[0] };
      }

      if (strategy.morningStarId?.length) {
        return { name: 'Morningstar ID', value: strategy.morningStarId[0] };
      }

      break;
    }
  }

  // Fallback to use index code, which should be the one to use in most case
  return { name: 'Index Code', value: strategy.code };
};

export const getSubtext = (
  strategy: IStrategy | StrategyItemResponseDTO,
  isPortfolioCompositionSection = false,
): string => {
  const { translate } = useTranslation();
  const { factor = '', assetClass = '' } = strategy;

  const assetClassTranslated =
    assetClass && !isPortfolioCompositionSection
      ? translate({
          path: 'GLOBAL.ASSET_CLASS_CONSTANTS',
          item: assetClass,
        })
      : '';

  const factorTranslated =
    factor && !isPortfolioCompositionSection
      ? ` | ${translate({
          path: 'GLOBAL.FACTOR_CONSTANTS_SUBTEXT',
          item: factor,
        })}`
      : '';

  let subtext = '';
  switch (strategy.type) {
    case TimeSeriesEntityTypeConstants.PUBLIC_THEMATIC:
    case TimeSeriesEntityTypeConstants.THEMATIC_BENCHMARK:
    case TimeSeriesEntityTypeConstants.THEMATIC:
      if (strategy.theme) {
        subtext = `${strategy.theme}`;
      }

      if (strategy.esg === 'Yes') {
        subtext = `${subtext} | ESG`;
      }
      break;
    default: {
      subtext = `${assetClassTranslated}${factorTranslated}`;
      break;
    }
  }
  return subtext;
};

export const getAdditionalSubtext = ({
  strategy,
  regressorFxType,
  shouldUseGlobalFxCurrency,
}: {
  strategy: IStrategy | StrategyItemResponseDTO;
  regressorFxType?: FxConversion;
  /**
   * This should be false for cases in which we always want to show the underlying strategy's currency
   * e.g., on the Discover Table, the AnalysisToolModal or the DSB, etc
   */
  shouldUseGlobalFxCurrency?: boolean;
}): string => {
  const { translate } = useTranslation();
  const { toCurrency } = useFxConversion();
  const { returnType = null, returnCategory = null, currency = '' } = strategy;

  const returnTypeTranslated = returnType
    ? translate({
        path: 'GLOBAL.RETURN_TYPE_CONSTANTS_SUBTEXT',
        item: returnType,
      })
    : '';

  const returnCategoryTranslated = returnCategory
    ? translate({
        path: 'GLOBAL.RETURN_CATEGORY_CONSTANTS_SUBTEXT',
        item: returnCategory,
      })
    : '';

  let subtext = '';
  subtext = `${shouldUseGlobalFxCurrency ? toCurrency.value : currency} `;
  // On the regression page, when fx conversion is applied and it is of type Monthly Hedged, we want to show it
  if (regressorFxType && toCurrency.value !== currency && regressorFxType === FxConversion.MONTHLY_HEDGED) {
    subtext = `${subtext} ${translate({
      path: 'GLOBAL.FX_CONVERSION.MONTHLY_HEDGED_SHORT',
    })} `;
  }

  if (returnCategoryTranslated) {
    subtext = `${subtext}${returnCategoryTranslated}`;
  }

  if (returnTypeTranslated) {
    subtext = `${subtext}${returnTypeTranslated}`;
  }

  return subtext;
};

type SupportedStrategyProperties =
  | ATTRIBUTE_DATABASE_NAME.AssetClass
  | ATTRIBUTE_DATABASE_NAME.Factor
  | ATTRIBUTE_DATABASE_NAME.Region
  | ATTRIBUTE_DATABASE_NAME.Style
  | ATTRIBUTE_DATABASE_NAME.IntradayType
  | ATTRIBUTE_DATABASE_NAME.ReturnCategory
  | ATTRIBUTE_DATABASE_NAME.ReturnType
  | ATTRIBUTE_DATABASE_NAME.LongShort
  | ATTRIBUTE_PROPER_NAME.AssetClass
  | ATTRIBUTE_PROPER_NAME.Factor
  | ATTRIBUTE_PROPER_NAME.Region
  | ATTRIBUTE_PROPER_NAME.Style
  | ATTRIBUTE_PROPER_NAME.ReturnCategory
  | ATTRIBUTE_PROPER_NAME.ReturnType;

/**
 * Translates the values of a subset of strategy properties.
 *
 * @param property
 *   Currently supported properties are `assetClass`, `factor`, `region`, `style`, `intradayType`, `returnCategory`, and `returnType`.
 *
 * @param value
 *   The value of the property to be translated. If `null` or `undefined`, behavior depends
 *   on the `isReturnOriginalValIfNull` parameter.
 *
 * @param isReturnOriginalValIfNull
 *   Optional. If `true`, the function returns the original `value` when it is `null` or `undefined`.
 *   Otherwise, it returns `undefined` (default behavior for backward compatibility).
 *
 * @returns
 *   - Translated label for the property value if `value` is non-null and the property is supported.
 *   - If `value` is `null` or `undefined`:
 *     - Returns the original `value` if `isReturnOriginalValIfNull` is `true`.
 *     - Returns `undefined` if `isReturnOriginalValIfNull` is `false` or omitted.
 *   - Returns `undefined` for unsupported properties.
 */
export const translateKnownStrategyProperty = (
  property: string,
  value: string | null,
  isReturnOriginalValIfUnknown?: boolean,
): string | undefined | null => {
  const { translate } = useTranslation();

  const translationMap: Record<SupportedStrategyProperties, string> = {
    [ATTRIBUTE_DATABASE_NAME.AssetClass]: 'GLOBAL.ASSET_CLASS_CONSTANTS',
    [ATTRIBUTE_DATABASE_NAME.Factor]: 'GLOBAL.FACTOR_CONSTANTS',
    [ATTRIBUTE_DATABASE_NAME.Region]: 'GLOBAL.REGION_CONSTANTS',
    [ATTRIBUTE_DATABASE_NAME.Style]: 'GLOBAL.STYLE_CONSTANTS',
    [ATTRIBUTE_DATABASE_NAME.IntradayType]: 'GLOBAL.INTRADAY_TYPE_CONSTANTS',
    [ATTRIBUTE_DATABASE_NAME.ReturnCategory]: 'GLOBAL.RETURN_CATEGORY_CONSTANTS',
    [ATTRIBUTE_DATABASE_NAME.ReturnType]: 'GLOBAL.RETURN_TYPE_CONSTANTS',
    [ATTRIBUTE_DATABASE_NAME.LongShort]: 'GLOBAL.LONG_SHORT_CONSTANTS',

    [ATTRIBUTE_PROPER_NAME.AssetClass]: 'GLOBAL.ASSET_CLASS_CONSTANTS',
    [ATTRIBUTE_PROPER_NAME.Factor]: 'GLOBAL.FACTOR_CONSTANTS',
    [ATTRIBUTE_PROPER_NAME.Region]: 'GLOBAL.REGION_CONSTANTS',
    [ATTRIBUTE_PROPER_NAME.Style]: 'GLOBAL.STYLE_CONSTANTS',
    [ATTRIBUTE_PROPER_NAME.ReturnCategory]: 'GLOBAL.RETURN_CATEGORY_CONSTANTS',
    [ATTRIBUTE_PROPER_NAME.ReturnType]: 'GLOBAL.RETURN_TYPE_CONSTANTS',
  };

  if (property in translationMap) {
    return value
      ? translate({
          path: translationMap[property as keyof typeof translationMap],
          item: value,
        })
      : '';
  }

  if (property === ATTRIBUTE_DATABASE_NAME.IsLive) {
    return value
      ? translate({
          path: 'NAVIGATION_PANEL.BASKET_STATUS.LIVE',
        })
      : undefined;
  }

  // Return behavior based on the isReturnOriginalValIfUnknown flag
  return isReturnOriginalValIfUnknown ? value : undefined;
};

export type TranslateResult<T extends string> = { value: T; label: string };
export type Translator<T extends string> = (key: T) => TranslateResult<T>;

/**
 * Fetches a Translator for a given property.
 *
 * A translator maps a given property value into its label.
 *
 * For a given strategy property `T`, a translator can be passed into `(T[]).map()`
 * to convert an array of its values to a `TranslateResult` (`{ value: T; label: string }`).
 *
 * Example:
 * ````ts
 * const availableAssetClassFilters =
 *   Object.values(AssetClassConstants).map(translatorForProperty(ATTRIBUTE_DATABASE_NAME.AssetClass));
 * // [{ value: AssetClassConstants.MULTI_ASSETS, label: 'Multi Assets' }, ... ]
 * ````
 *
 * @param property
 *   Currently supported properties are `assetClass`, `factor`, `region`, `style`, `intradayType`, `returnCategory` and `returnType`.
 *
 * @returns
 *   Translator for given property.
 *   Unknown properties return a passthrough Translator which leaves the label same as the value.
 *
 * Todo: Move to other class or convert to composable when we support portfolio properties.
 */
export const translatorForProperty = <T extends string>(property: ATTRIBUTE_DATABASE_NAME): Translator<T> => {
  return (key: T) => {
    const label = translateKnownStrategyProperty(property, key);
    return { value: key, label: label ?? key };
  };
};

export const sortStrategies = (strategies: DSBStrategyItemResponseDTO[]): DSBStrategyItemResponseDTO[] => {
  return strategies
    .map((strategy) => [strategy.displayName.toLowerCase(), strategy] as const)
    .sort(([aDisplayName], [bDisplayName]) => (aDisplayName > bDisplayName ? 1 : -1))
    .map(([, strategy]) => strategy);
};

export const getStrategyDisplayNameBasedOnPreference = ({
  strategy,
  preference,
}: {
  strategy: IPortfolioTreeStrategyStaticInfo | StrategyItemResponseDTO | StrategyItemSubset | StrategyDisplayNameSource;
  preference?: DisplayName;
}): string => {
  switch (preference) {
    case DisplayName.SHORT_NAME:
    case undefined: {
      return (strategy.shortName || strategy.longName || getStrategyDisplayCode(strategy))?.trim();
    }
    case DisplayName.LONG_NAME: {
      return (strategy.longName || strategy.shortName || getStrategyDisplayCode(strategy))?.trim();
    }
    case DisplayName.TICKER_SHORT_NAME: {
      if (getStrategyDisplayCode(strategy, false) && strategy.shortName) {
        return `${getStrategyDisplayCode(strategy, false)} | ${strategy.shortName}`?.trim();
      }

      return (strategy.shortName || strategy.longName || getStrategyDisplayCode(strategy))?.trim();
    }
    case DisplayName.CODE: {
      // Todo: support masking datum codes
      return getStrategyDisplayCode(strategy, true)?.trim();
    }
  }
};

export const sortItemsByDisplayName = <T extends { displayName: string }>(data: T[]): T[] => {
  return data
    .map((datum) => [datum.displayName.toLowerCase(), datum] as const)
    .sort(([aDisplayName], [bDisplayName]) => aDisplayName.localeCompare(bDisplayName))
    .map(([, datum]) => datum);
};

export const sortCash = (cash: StrategyItemResponseDTO[]): StrategyItemResponseDTO[] => {
  return cash
    .map((cash) => [cash.code.toLowerCase(), cash] as const)
    .sort(([aCode], [bCode]) => (aCode > bCode ? 1 : -1))
    .map(([, cash]) => cash);
};

export const sortBenchmarks = (
  benchmarks: (StrategyItemResponseDTO | PortfolioItemResponseDTO)[],
): (StrategyItemResponseDTO | PortfolioItemResponseDTO)[] => {
  return benchmarks
    .map((benchmark) => {
      const identifier = 'slug' in benchmark ? benchmark.name : benchmark.shortName;
      return [identifier.toLowerCase(), benchmark] as const;
    })
    .sort(([aIdentifier], [bIdentifier]) => (aIdentifier > bIdentifier ? 1 : -1))
    .map(([, benchmark]) => benchmark);
};

export const sortSignals = (signals: SignalListItemResponseDTO[]): SignalListItemResponseDTO[] => {
  return signals
    .map((signal) => [signal.shortName.toLowerCase(), signal] as const)
    .sort(([aShortName], [bShortName]) => (aShortName > bShortName ? 1 : -1))
    .map(([, signal]) => signal);
};

/**
 * Pick default ticker for stocks
 *
 * Tries to match the ticker against the strategy code, or returns the first ticker sorted alphabetically.
 */
export const pickDefaultTicker = (strategy: StrategyFactsheetBasicInfoDTO): string | undefined => {
  const prefix = strategy.code.replace('_', ' ');
  const matchedTicker = strategy.ticker.find((o) => o.startsWith(prefix));
  if (matchedTicker) return matchedTicker;

  const [firstTickerSorted] = [...strategy.ticker].sort();
  return firstTickerSorted;
};

/**
 * As suggested by Henry, US, CN, GR have issue on BofA
 * If the ticker's exchange or country code matches any of the PRIORITY_COUNTRY_EXCHANGE_CODES, then we should use that ticker
 * otherwise, we use the first available ticker
 */
export const getCompositeTicker = (tickers: string[] | undefined | null): string[] | undefined => {
  // A subset of composite exchange codes.
  const COMPOSITE_EXCHANGE_CODES = new Set(['US', 'CN', 'GR']);

  if (!Array.isArray(tickers) || !tickers.length) return undefined;

  const retval = tickers.find((item) => {
    const tickerCountryCode = item.split(' ')[0];
    if (COMPOSITE_EXCHANGE_CODES.has(tickerCountryCode)) return true;
  });

  // Todo: we really shouldn't be stripping the suffix here. The place which needs a short ticker should be the place
  // stripping out the suffix, not us here stripping it out pre-maturely in the global data layer, as we still want to
  // show it in various places such as the factsheet. (note: factsheet does not use the global data layer though)
  const tickerToUse = (retval ?? (tickers[0] || '-')).replace(/ Equity$/, '');

  return [tickerToUse];
};

/**
 * Extracts the name part from a display name that may include ticker format.
 * If the name is in "Ticker | Name" format, returns just the "Name" part.
 * Otherwise, returns the name as-is.
 */
export const extractNameFromTickerFormat = (displayName: string): string => {
  return displayName.includes(' | ') ? displayName.split(' | ')[1] : displayName;
};

export const hasMaskBeenApplied = (name: string, reference?: string | null, preference?: DisplayName): boolean => {
  if (!preference || !reference) return false;

  if (preference === DisplayName.TICKER_SHORT_NAME) {
    const parts = name.split(' | ');
    return parts.length > 1 && parts[0] === reference;
  }

  return false;
};

export const getPortfolioNameBasedOnPreference = ({
  portfolio,
  preference,
}: {
  portfolio: PortfolioItemResponseDTO | IPortfolioTreeSubportfolio;
  preference?: DisplayName;
}): string => {
  switch (preference) {
    case DisplayName.TICKER_SHORT_NAME: {
      // Check if the preference has been applied on the API side to avoid duplicating the reference
      const shouldApplyMask = !hasMaskBeenApplied(portfolio.name, portfolio.reference, preference);
      return portfolio.reference && shouldApplyMask ? `${portfolio.reference} | ${portfolio.name}` : portfolio.name;
    }
    default:
      return portfolio.name;
  }
};
